import { Task, FullProject } from '../../../types';
import { diffDays, todayStr, addDays } from '../../../utils/dateUtils';
import {
  isInboxProject,
  isCommProject,
  areProjectIdsMatching,
  isMatchingTaskId,
} from '../../../utils/idNormalizer';
import { normalizePersonName, computePersonKey } from '../../../components/communication/personModel';

export interface ApplyTaskUpdateAndContactSyncParams {
  id: number | string;
  originalTask: Task;
  finalUpdates: Partial<Task>;
  targetProject: FullProject;
  allProjects: FullProject[];
  COMM_PROJECT_ID: string;
  markProjectDirty: (id: string) => void;
  onMarkTaskDirty: (taskId: string | number, projectId?: string) => void;
  onContactCreated?: (newContact: Task) => void;
  onTaskMutated?: (mutatedTask: Task, projectId: string) => void;
}

/**
 * Enriches `finalUpdates` with existing contact metadata when an assignee changes,
 * attaches stable `contactId` / `personKey`, and applies the update across `allProjects`
 * including dependency date shifts, contact name/metadata propagation, and automatic
 * pool-contact creation in `COMM_PROJECT_ID`.
 */
export function applyProjectWideTaskUpdateAndContactSync({
  id,
  originalTask,
  finalUpdates,
  targetProject,
  allProjects,
  COMM_PROJECT_ID,
  markProjectDirty,
  onMarkTaskDirty,
  onContactCreated,
  onTaskMutated,
}: ApplyTaskUpdateAndContactSyncParams): FullProject[] {
  const newTargetAssignee = finalUpdates.assignee;
  if (newTargetAssignee && newTargetAssignee.trim() !== '' && newTargetAssignee !== originalTask?.assignee) {
    const normTarget = normalizePersonName(newTargetAssignee);
    const allSystemTasks = allProjects.flatMap(proj => proj.tasks || []);
    const existingTask = allSystemTasks
      .filter(oldT => {
        const oldNorm = normalizePersonName(oldT.name || oldT.assignee);
        return oldNorm === normTarget && (oldT.organization || oldT.contactEmail || oldT.role);
      })
      .sort((a, b) => {
        const depthA = (a.organization ? 1 : 0) + (a.contactEmail ? 1 : 0) + (a.contactPhone ? 1 : 0);
        const depthB = (b.organization ? 1 : 0) + (b.contactEmail ? 1 : 0) + (b.contactPhone ? 1 : 0);
        return depthB - depthA;
      })[0];

    if (existingTask) {
      finalUpdates.organization = finalUpdates.organization || existingTask.organization;
      finalUpdates.role = finalUpdates.role || existingTask.role;
      finalUpdates.contactEmail = finalUpdates.contactEmail || existingTask.contactEmail;
      finalUpdates.contactPhone = finalUpdates.contactPhone || existingTask.contactPhone;
      finalUpdates.socialCapital = finalUpdates.socialCapital || existingTask.socialCapital;
      finalUpdates.mood = finalUpdates.mood || existingTask.mood;
      finalUpdates.contactFunction = finalUpdates.contactFunction || existingTask.contactFunction;
      if (finalUpdates.contactId === undefined && (existingTask.type === 'contact' || existingTask.status === 'pool' || String(existingTask.id).startsWith('pool_contact_'))) {
        finalUpdates.contactId = existingTask.id;
      }
      if (finalUpdates.personKey === undefined) {
        finalUpdates.personKey = computePersonKey(existingTask);
      }
    }
  }

  const contactFields: Array<keyof Task> = ['organization', 'contactEmail', 'contactPhone', 'role', 'socialCapital', 'mood'];
  const contactUpdates: Partial<Task> = {};
  for (const f of contactFields) {
    if (f in finalUpdates) {
      Object.assign(contactUpdates, { [f]: finalUpdates[f] });
    }
  }
  const hasContactUpdates = Object.keys(contactUpdates).length > 0;
  const currentAssignee = finalUpdates.assignee || originalTask.assignee;
  const isStartingTimer = !!(finalUpdates.timerStartedAt && finalUpdates.timerStartedAt > 0 && !originalTask.timerStartedAt);

  // Contact Name & Email Sync: If a contact person is renamed or their email changes, propagate new key and metadata to linked Vorgänge & tasks
  const isContactEntity = originalTask.type === 'contact' || originalTask.status === 'pool' || String(originalTask.id).startsWith('pool_contact_');
  const oldContactName = (originalTask.name || originalTask.assignee || '').trim();
  const oldNorm = normalizePersonName(oldContactName);
  const newContactName = (
    finalUpdates.name !== undefined
      ? finalUpdates.name
      : (finalUpdates.assignee !== undefined ? finalUpdates.assignee : oldContactName)
  ).trim();
  const isContactNameChanged = isContactEntity && !!newContactName && newContactName !== oldContactName;
  const isContactEmailChanged = isContactEntity && finalUpdates.contactEmail !== undefined && finalUpdates.contactEmail !== originalTask.contactEmail;

  if (isContactEntity && (isContactNameChanged || isContactEmailChanged)) {
    const effEmail = finalUpdates.contactEmail !== undefined ? finalUpdates.contactEmail : originalTask.contactEmail;
    const effName = newContactName || oldContactName;
    finalUpdates.personKey = computePersonKey({ contactEmail: effEmail, name: effName });
  }
  const currentNorm = normalizePersonName(currentAssignee);

  // Contact Completion Sync: When a communication Vorgang is completed, update lastContactDate on the linked contact
  const isCompletingVorgang = !!(
    (finalUpdates.completed === true || finalUpdates.status === 'completed') &&
    !originalTask.completed &&
    (originalTask.category === 'Kommunikation' || originalTask.contactId)
  );

  // Assignee -> Contact Record & Project Association Sync
  const isAssigneeChanged = !isContactEntity && finalUpdates.assignee !== undefined && finalUpdates.assignee !== originalTask.assignee;
  const assignedPersonNames = isAssigneeChanged
    ? (finalUpdates.assignee || '').split(/[,;/]+/).map(s => s.trim()).filter(s => s && s !== 'all')
    : [];
  const assignedPersonNorms = new Set(assignedPersonNames.map(n => normalizePersonName(n)).filter(Boolean));
  const isRealTargetProj = Boolean(targetProject && !isCommProject(targetProject.id) && !isInboxProject(targetProject.id));

  const existingContactByNorm = new Map<string, Task>();
  if (assignedPersonNorms.size > 0) {
    allProjects.forEach(p => {
      (p.tasks || []).forEach(t => {
        if (t.archived) return;
        if (t.type === 'contact' || t.status === 'pool' || String(t.id).startsWith('pool_contact_')) {
          const norm = normalizePersonName(t.name || t.assignee);
          if (norm && !existingContactByNorm.has(norm)) {
            existingContactByNorm.set(norm, t);
          }
        }
      });
    });
  }

  const missingContactNames = assignedPersonNames.filter(name => {
    const norm = normalizePersonName(name);
    return Boolean(norm && !existingContactByNorm.has(norm));
  });
  const missingContactIdByNorm = new Map<string, string>();
  missingContactNames.forEach((personName, idx) => {
    const norm = normalizePersonName(personName);
    if (norm && !missingContactIdByNorm.has(norm)) {
      missingContactIdByNorm.set(
        norm,
        `${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 6)}`
      );
    }
  });

  // Attach stable contactId & personKey on regular tasks when assignee changes
  if (isAssigneeChanged) {
    if (assignedPersonNames.length > 0) {
      const primaryName = assignedPersonNames[0];
      const primaryNorm = normalizePersonName(primaryName);
      const existingPrimary = primaryNorm ? existingContactByNorm.get(primaryNorm) : undefined;
      const resolvedContactId = existingPrimary
        ? String(existingPrimary.id)
        : (primaryNorm ? missingContactIdByNorm.get(primaryNorm) : undefined);

      if (resolvedContactId && finalUpdates.contactId === undefined) {
        finalUpdates.contactId = resolvedContactId;
      }
      const resolvedKey = computePersonKey({
        contactEmail: existingPrimary?.contactEmail || finalUpdates.contactEmail || originalTask.contactEmail,
        name: primaryName,
      });
      if (resolvedKey && finalUpdates.personKey === undefined) {
        finalUpdates.personKey = resolvedKey;
      }
    } else if (originalTask.category !== 'Kommunikation' && finalUpdates.contactId === undefined) {
      finalUpdates.contactId = undefined;
      finalUpdates.personKey = undefined;
    }
  }

  return allProjects.map(p => {
    let modified = false;

    const isTargetProject = p.id === targetProject.id;
    const isCommProj = isCommProject(p.id) || areProjectIdsMatching(p.id, COMM_PROJECT_ID);
    const couldHaveDependencies = finalUpdates.end && originalTask.end && finalUpdates.end !== originalTask.end;

    if (
      !isTargetProject &&
      !hasContactUpdates &&
      !couldHaveDependencies &&
      !isStartingTimer &&
      !isContactNameChanged &&
      !isContactEmailChanged &&
      !isCompletingVorgang &&
      !(assignedPersonNorms.size > 0 && (isRealTargetProj || (isCommProj && missingContactNames.length > 0)))
    ) {
      return p;
    }

    const recordTaskMutation = (mutated: Task): Task => {
      modified = true;
      onMarkTaskDirty(mutated.id, p.id);
      onTaskMutated?.(mutated, p.id);
      return mutated;
    };

    const updatedTasks = p.tasks.map(t => {
      if (isTargetProject && isMatchingTaskId(id, t.id, p.id)) {
        return recordTaskMutation({ ...t, ...finalUpdates, updatedAt: Date.now() });
      }

      if (isRealTargetProj && assignedPersonNorms.size > 0 && (t.type === 'contact' || t.status === 'pool')) {
        const norm = normalizePersonName(t.name || t.assignee);
        if (norm && assignedPersonNorms.has(norm)) {
          const existingPids = Array.isArray(t.projectIds) ? t.projectIds : [];
          if (!existingPids.some(pid => areProjectIdsMatching(pid, targetProject.id))) {
            return recordTaskMutation({
              ...t,
              projectIds: [...existingPids, targetProject.id],
              updatedAt: Date.now(),
            });
          }
        }
      }

      if (isStartingTimer && t.timerStartedAt && t.timerStartedAt > 0) {
        const elapsed = Date.now() - t.timerStartedAt;
        return recordTaskMutation({
          ...t,
          timerStartedAt: null,
          trackedTime: (t.trackedTime || 0) + elapsed,
          updatedAt: Date.now()
        });
      }

      if (couldHaveDependencies) {
        const diff = diffDays(originalTask.end, finalUpdates.end!);
        if (diff > 0 && t.dependencies && t.dependencies.includes(String(id))) {
          const hasDate = !!(t.start?.trim() || t.end?.trim());
          const duration = hasDate && t.start && t.end ? diffDays(t.start, t.end) : 0;
          const currentStart = hasDate ? (t.start || todayStr) : null;
          const newStart = currentStart ? addDays(currentStart, diff) : null;
          const newEnd = newStart ? addDays(newStart, duration) : null;

          if (newStart || newEnd) {
            return recordTaskMutation({ ...t, start: newStart || t.start, end: newEnd || t.end, updatedAt: Date.now() });
          }
        }
      }

      if (hasContactUpdates && currentNorm) {
        const tName = (t.type === 'contact' ? (t.name || t.assignee) : t.assignee) || '';
        if (tName && normalizePersonName(tName) === currentNorm) {
          const nextEmail = contactUpdates.contactEmail !== undefined ? contactUpdates.contactEmail : t.contactEmail;
          const nextPersonKey = computePersonKey({ contactEmail: nextEmail, name: tName });
          return recordTaskMutation({
            ...t,
            ...contactUpdates,
            ...(nextPersonKey ? { personKey: nextPersonKey } : {}),
            updatedAt: Date.now(),
          });
        }
      }

      if ((isContactNameChanged || isContactEmailChanged) && oldNorm) {
        const matchesId = String(t.contactId) === String(id);
        const tName = (t.type === 'contact' ? (t.name || t.assignee) : t.assignee) || '';
        const assigneeParts = t.type !== 'contact' && tName
          ? tName.split(/[,;/]+/).map(s => s.trim()).filter(Boolean)
          : [];
        const hasMatchingAssigneeToken = assigneeParts.some(part => normalizePersonName(part) === oldNorm);
        const matchesName = Boolean(tName && (normalizePersonName(tName) === oldNorm || hasMatchingAssigneeToken));

        if (matchesId || matchesName) {
          const effContactName = isContactNameChanged ? newContactName : (t.type === 'contact' ? (t.name || t.assignee || '') : tName);
          const effEmail = contactUpdates.contactEmail !== undefined ? contactUpdates.contactEmail : t.contactEmail;
          const nextPersonKey = computePersonKey({ contactEmail: effEmail, name: effContactName });
          if (t.type === 'contact') {
            return recordTaskMutation({
              ...t,
              ...(isContactNameChanged ? { name: newContactName, assignee: newContactName } : {}),
              ...(nextPersonKey ? { personKey: nextPersonKey } : {}),
              ...contactUpdates,
              updatedAt: Date.now(),
            });
          }
          const updatedAssignee = isContactNameChanged
            ? (assigneeParts.length > 1
                ? assigneeParts.map(part => (normalizePersonName(part) === oldNorm ? newContactName : part)).join(', ')
                : newContactName)
            : t.assignee;
          return recordTaskMutation({
            ...t,
            assignee: updatedAssignee,
            ...(matchesId || assigneeParts.length <= 1 ? { personKey: nextPersonKey || t.personKey } : {}),
            ...contactUpdates,
            updatedAt: Date.now(),
          });
        }
      }

      if (isCompletingVorgang && (
        (originalTask.contactId && String(t.id) === String(originalTask.contactId)) ||
        (t.type === 'contact' && currentNorm && normalizePersonName(t.name || t.assignee) === currentNorm)
      )) {
        return recordTaskMutation({ ...t, lastContactDate: todayStr, updatedAt: Date.now() });
      }

      return t;
    });

    if (isCommProj && missingContactNames.length > 0) {
      const createdContacts: Task[] = missingContactNames.map((personName, idx) => {
        const norm = normalizePersonName(personName);
        const newContactId = (norm && missingContactIdByNorm.get(norm)) || `${Date.now()}_${idx}_${Math.random().toString(36).substring(2, 6)}`;
        const newContact: Task = {
          id: newContactId,
          createdAt: Date.now(),
          updatedAt: Date.now(),
          name: personName,
          assignee: personName,
          personKey: computePersonKey({
            contactEmail: finalUpdates.contactEmail || originalTask.contactEmail,
            name: personName,
          }),
          role: finalUpdates.role || originalTask.role,
          organization: finalUpdates.organization || originalTask.organization,
          contactEmail: finalUpdates.contactEmail || originalTask.contactEmail,
          contactPhone: finalUpdates.contactPhone || originalTask.contactPhone,
          type: 'contact',
          status: 'pool',
          start: '',
          end: '',
          category: 'Kommunikation',
          priority: 'none',
          completed: false,
          workspace: targetProject?.workspace || 'professional',
          projectId: p.id,
          ...(isRealTargetProj ? { projectIds: [targetProject.id] } : {}),
          isInlineNew: false,
          sortOrder: (updatedTasks.length + idx + 1) * 1000,
        };
        onMarkTaskDirty(newContact.id, p.id);
        onContactCreated?.(newContact);
        return newContact;
      });
      modified = true;
      markProjectDirty(p.id);
      return { ...p, tasks: [...updatedTasks, ...createdContacts], lastModified: Date.now() };
    }

    if (modified) {
      markProjectDirty(p.id);
      return { ...p, tasks: updatedTasks, lastModified: Date.now() };
    }
    return p;
  });
}
